import { ViewportScroller } from '@angular/common';
import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { Project } from '../../../features/projects/interfaces/project.interface';
import { GlobalStoreService } from '../../../global/services/global-store.service';
import { MASONRY_COLUMNS } from '../../constants/constants';

@Component({
  selector: 'app-filtered-projects',
  templateUrl: './filtered-projects.component.html',
  styleUrl: './filtered-projects.component.scss',
  standalone: false
})
export class FilteredProjectsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() expertise!: string | undefined;
  @Input() selectedProjectId: number | null | undefined = null;
  @Input() limit: number | null = null;
  public isListOfColumnEmpty = false;
  public columns: string[] = MASONRY_COLUMNS;
  public listOfColumnItems: any = {
    firstColumn: [],
    secondColumn: [],
    thirdColumn: [],
    fourthColumn: [],
  };
  public warningMessage = 'No projects are present!';
  public dataSource: any = [];
  private readonly destroy$ = new Subject<void>();

  constructor(
    private globalStore: GlobalStoreService,
    private viewportScroller: ViewportScroller,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.globalStore
      .selectAllProjects()
      .pipe(takeUntil(this.destroy$))
      .subscribe((projects: Project[]) => {
        this.dataSource = projects;
        this.restListOfColumnItems();
        this.initFilter();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['expertise'] && !changes['expertise'].firstChange) {
      this.initFilter();
    }
  }

  private restListOfColumnItems(): void {
    this.listOfColumnItems['firstColumn'] = [];
    this.listOfColumnItems['secondColumn'] = [];
    this.listOfColumnItems['thirdColumn'] = [];
    this.listOfColumnItems['fourthColumn'] = [];
  }

  private populateArticles(projects: Project[]): void {
    const numColumns = this.limit === 3 ? 3 : 4;
    const totalArticles = projects.length;
    const articlesPerColumn = Math.floor(totalArticles / numColumns);
    const extraArticles = totalArticles % numColumns;

    let column = 0;
    let articlesAdded = 0;

    projects.forEach((project) => {
      if (
        articlesAdded >=
        articlesPerColumn + (column < extraArticles ? 1 : 0)
      ) {
        column++;
        articlesAdded = 0;
      }
      switch (column) {
        case 0:
          this.listOfColumnItems.firstColumn.push(project);
          break;
        case 1:
          this.listOfColumnItems.secondColumn.push(project);
          break;
        case 2:
          this.listOfColumnItems.thirdColumn.push(project);
          break;
        case 3:
          this.listOfColumnItems.fourthColumn.push(project);
          break;
      }
      articlesAdded++;
    });
    this.isListOfColumnEmpty =
      !this.listOfColumnItems['firstColumn'].length &&
      !this.listOfColumnItems['secondColumn'].length &&
      !this.listOfColumnItems['thirdColumn'].length &&
      !this.listOfColumnItems['fourthColumn'].length;
  }

  public initFilterByExpertiseSelected(): void {
    this.initFilter();
    this.viewportScroller.scrollToPosition([0, 0]);
  }

  public initFilter(): void {
    let filteredData = [...this.dataSource];

    // Sort by ID descending to get most recent first
    filteredData.sort((a, b) => b.id - a.id);

    if (this.selectedProjectId) {
      filteredData = filteredData.filter(
        (project: any) => project.id !== this.selectedProjectId
      );
    }

    const expertisesFilterValue = this.expertise;

    let filteredDataSource = filteredData.filter((project: Project) => {
      if (!expertisesFilterValue || !project.expertise) return true;
      const filter = expertisesFilterValue.toLowerCase().trim();
      const projExp = project.expertise.toLowerCase().trim();
      return projExp === filter || projExp.includes(filter);
    });

    if (this.limit) {
      filteredDataSource = filteredDataSource.slice(0, this.limit);
    }

    this.restListOfColumnItems();
    this.populateArticles(filteredDataSource);
  }

  public getSelectedArticle(project: any): void {
    this.router.navigate(['projects/project', project.id, project.title]);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
